/* ============================================================================
 * DaySummaryPage — /dashboard/day-summary (owner). Migration 117.
 *
 * The end-of-day summary: the day's labor, materials bought, the rebate
 * tracker (Menards cash back called out), what needs attention and the good
 * news. The end-of-day phone notification opens this page; the arrows step
 * back through the last 90 days. Numbers come from the account's own
 * records (owner_day_summary_get); nothing here writes.
 * ========================================================================== */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  PackageOpen,
  PartyPopper,
  PiggyBank,
} from 'lucide-react';
import PageHeader from '@/components/dashboard/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { fetchDaySummary } from '@/lib/reminders';

const usd = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Number.isInteger(v) || Math.abs(v) >= 1000 ? 0 : 2,
  });
};
const hrs = (n) => (Number(n) || 0).toFixed(1);

const shiftDay = (iso, by) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + by);
  return d.toISOString().slice(0, 10);
};

const Section = ({ Icon, title, tone, children, testId }) => (
  <Card className="min-w-0 border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid={testId}>
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon
          size={16}
          className={cn(
            'shrink-0',
            tone === 'alert' ? 'text-[hsl(var(--destructive))]' : tone === 'good' ? 'text-emerald-600' : 'text-[hsl(var(--primary))]'
          )}
        />
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-foreground">{title}</h2>
      </div>
      {children}
    </CardContent>
  </Card>
);

const Big = ({ value, label }) => (
  <div className="min-w-0">
    <div className="text-2xl font-semibold tabular-nums text-foreground">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

const Row = ({ left, right }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 text-sm border-t border-border first:border-t-0">
    <span className="text-foreground min-w-0 truncate">{left}</span>
    <span className="text-muted-foreground tabular-nums shrink-0">{right}</span>
  </div>
);

export default function DaySummaryPage() {
  const { t, i18n } = useTranslation();
  const [day, setDay] = useState(null); // null = today (account time zone)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetchDaySummary(d);
      if (res?.ok === false) throw new Error(res.error);
      setData(res);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(day);
  }, [day, load]);

  const shownDay = data?.day || null;
  const dateLabel = shownDay
    ? new Date(`${shownDay}T12:00:00`).toLocaleDateString(i18n.language === 'es' ? 'es-US' : 'en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      })
    : '';

  const l = data?.labor || {};
  const m = data?.materials || {};
  const r = data?.rebates || {};
  const attention = Array.isArray(data?.attention) ? data.attention : [];
  const good = Array.isArray(data?.good_news) ? data.good_news : [];
  const materialsTotal = (Number(m.receipts_total) || 0) + (Number(m.other_total) || 0);

  return (
    <div data-testid="day-summary-page">
      <PageHeader
        eyebrow={t('daySummary.eyebrow')}
        title={data?.is_today ? t('daySummary.title_today') : dateLabel || t('daySummary.title_today')}
        subtitle={data?.is_today ? dateLabel : t('daySummary.subtitle_past')}
        actions={
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 border-border"
              onClick={() => shownDay && setDay(shiftDay(shownDay, -1))}
              disabled={loading || !shownDay}
              aria-label={t('daySummary.prev_day')}
              data-testid="day-summary-prev"
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-10 border-border"
              onClick={() => setDay(null)}
              disabled={loading || data?.is_today}
              data-testid="day-summary-today"
            >
              {t('daySummary.today')}
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 border-border"
              onClick={() => shownDay && setDay(shiftDay(shownDay, 1))}
              disabled={loading || !shownDay || data?.is_today}
              aria-label={t('daySummary.next_day')}
              data-testid="day-summary-next"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        }
      />

      {loading && !data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('daySummary.loading')}
        </div>
      ) : error ? (
        <p className="text-sm text-[hsl(var(--destructive))] py-6" role="alert">
          {t('daySummary.load_error')}
        </p>
      ) : (
        <div className={cn('grid gap-4 grid-cols-1 md:grid-cols-2', loading && 'opacity-60')}>
          <Section Icon={Clock} title={t('daySummary.labor')} testId="day-summary-labor">
            {Number(l.hours) > 0 ? (
              <>
                <div className="flex flex-wrap gap-x-8 gap-y-3 mb-3">
                  <Big value={usd(l.cost)} label={t('daySummary.labor_cost')} />
                  <Big value={hrs(l.hours)} label={t('daySummary.hours')} />
                  <Big value={l.people || 0} label={t('daySummary.people')} />
                </div>
                {Number(l.sub_hours) > 0 ? (
                  <p className="text-xs text-muted-foreground mb-2">
                    {t('daySummary.crew_vs_subs', { crew: usd(l.crew_cost), subs: usd(l.sub_cost) })}
                  </p>
                ) : null}
                {(l.by_job || []).map((j) => (
                  <Row key={j.job} left={j.job} right={`${usd(j.cost)} · ${hrs(j.hours)} h`} />
                ))}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('daySummary.no_hours')}</p>
            )}
            {Number(l.on_clock) > 0 ? (
              <p className="mt-2 text-sm text-[hsl(var(--destructive))]">
                {t('daySummary.on_clock', { count: Number(l.on_clock) })}
              </p>
            ) : null}
          </Section>

          <Section Icon={PackageOpen} title={t('daySummary.materials')} testId="day-summary-materials">
            {materialsTotal > 0 ? (
              <>
                <div className="flex flex-wrap gap-x-8 gap-y-3 mb-3">
                  <Big value={usd(materialsTotal)} label={t('daySummary.materials_total')} />
                  <Big value={m.receipts || 0} label={t('daySummary.receipts')} />
                </div>
                {(m.by_store || []).map((s) => (
                  <Row key={s.store} left={s.store} right={usd(s.total)} />
                ))}
                {Number(m.other_total) > 0 ? (
                  <Row left={t('daySummary.other_materials')} right={usd(m.other_total)} />
                ) : null}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('daySummary.no_materials')}</p>
            )}
          </Section>

          <Section Icon={PiggyBank} title={t('daySummary.rebates')} testId="day-summary-rebates">
            {Number(r.menards_today) > 0 ? (
              <div className="mb-3">
                <Big value={`+${usd(r.menards_today)}`} label={t('daySummary.menards_today')} />
              </div>
            ) : Number(r.earned_today) > 0 ? (
              <div className="mb-3">
                <Big value={`+${usd(r.earned_today)}`} label={t('daySummary.earned_today')} />
              </div>
            ) : null}
            <Row left={t('daySummary.ready_to_send', { count: Number(r.ready_count) || 0 })} right={usd(r.ready_total)} />
            <Row left={t('daySummary.waiting', { count: Number(r.waiting_count) || 0 })} right={usd(r.waiting_total)} />
            <Row left={t('daySummary.received_month')} right={usd(r.received_month)} />
            {Number(r.menards_received_year) > 0 ? (
              <Row left={t('daySummary.menards_year')} right={usd(r.menards_received_year)} />
            ) : null}
            <Link
              to="/dashboard/rebate-tracker"
              className="mt-3 inline-block text-xs text-[hsl(var(--primary))] underline underline-offset-2"
            >
              {t('daySummary.open_rebates')}
            </Link>
          </Section>

          <div className="grid gap-4 min-w-0">
            {data?.is_today ? (
              <Section Icon={AlertTriangle} title={t('daySummary.attention')} tone="alert" testId="day-summary-attention">
                {attention.length ? (
                  <ul className="space-y-1.5 text-sm text-foreground">
                    {attention.map((a) => (
                      <li key={a}>• {a}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('daySummary.nothing_attention')}</p>
                )}
              </Section>
            ) : null}
            <Section Icon={PartyPopper} title={t('daySummary.good_news')} tone="good" testId="day-summary-good">
              {good.length ? (
                <ul className="space-y-1.5 text-sm text-foreground">
                  {good.map((g) => (
                    <li key={g}>• {g}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">{t('daySummary.no_good_news')}</p>
              )}
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}
